import { useEffect, useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/solid';
import { cn } from '@restworld/utils-common';

export type AvatarSize = 'md' | 'lg';

export interface AvatarProps {
  src?: string;
  firstName?: string;
  lastName?: string;
  size?: AvatarSize;
  alt?: string;
}

export const Avatar = ({ src, firstName, lastName, size = 'md', alt = '' }: AvatarProps) => {
  const [imgFailed, setImgFailed] = useState(false);

  // Reset error state whenever the image source changes, otherwise switching
  // between candidates with the same Avatar instance can leave us stuck on
  // the initials fallback for a perfectly valid new URL.
  useEffect(() => {
    setImgFailed(false);
  }, [src]);

  const initials = getInitials(firstName, lastName);

  return (
    <div
      className={cn(
        'relative rounded-lg bg-purple-50 grid place-items-center overflow-hidden',
        size === 'md' ? 'w-16 h-16' : 'w-[88px] h-[88px]'
      )}
    >
      {src && !imgFailed ? (
        <img
          src={src}
          alt={alt}
          onError={() => setImgFailed(true)}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      ) : initials ? (
        <span className={cn('font-semibold text-purple-700 select-none', size === 'md' ? 'text-xl' : 'text-3xl')}>
          {initials}
        </span>
      ) : (
        <PhotoIcon className={cn('fill-purple-700', size === 'lg' ? 'h-12 w-12' : 'h-6 w-6')} />
      )}
    </div>
  );
};

export default Avatar;

function getInitials(firstName?: string, lastName?: string): string | null {
  const first = firstLetter(firstName);
  const last = firstLetter(lastName);
  const combined = `${first}${last}`;
  return combined.length > 0 ? combined.toUpperCase() : null;
}

function firstLetter(value?: string): string {
  if (!value) return '';
  const stripped = value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  return stripped.charAt(0);
}
